/**
 * The canonical registry: one classified record per native Parcel tool.
 *
 * MCP, Spec, Settings, and the generated docs all read from here. A descriptor
 * without metadata (or metadata without a descriptor) throws at module load, so
 * an unclassified tool cannot reach a model or ship in a build.
 */
import { NATIVE_TOOL_DESCRIPTORS } from './native-descriptors.js';
import { NATIVE_TOOL_METADATA, PREVIEWER_KEYS, REDACTOR_KEYS } from './native-metadata.js';
import type { NativeToolDefinition, NativeToolEffect, NativeToolSurface } from './types.js';

export { PREVIEWER_KEYS, REDACTOR_KEYS };
export type { PreviewerKey, RedactorKey } from './native-metadata.js';

/**
 * Key-sorted JSON so the version depends on content, not on the order the
 * object literal happened to be written in.
 */
export function stableStringify(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'null';
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  const entries = Object.entries(value as Record<string, unknown>)
    .filter(([, v]) => v !== undefined)
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${stableStringify(v)}`).join(',')}}`;
}

const FNV_OFFSET = 0xcbf29ce484222325n;
const FNV_PRIME = 0x100000001b3n;
const MASK_64 = 0xffffffffffffffffn;

// FNV-1a over UTF-16 code units. Synchronous and dependency-free: crypto.subtle
// is async, and this runs at module load in the browser bundle and the Workers
// runtime alike. It is a change detector, not a security primitive.
export function fnv1a64(input: string): string {
  let hash = FNV_OFFSET;
  for (let i = 0; i < input.length; i++) {
    hash = (hash ^ BigInt(input.charCodeAt(i))) & MASK_64;
    hash = (hash * FNV_PRIME) & MASK_64;
  }
  return hash.toString(16).padStart(16, '0');
}

/**
 * The immutable contract version. Covers everything a caller can act on: the
 * name, the input schema, and the classification that governs it. Descriptions
 * are excluded so wording changes do not invalidate a pending approval.
 */
export function nativeToolContractVersion(
  tool: Pick<
    NativeToolDefinition,
    | 'descriptor'
    | 'effect'
    | 'defaultPolicy'
    | 'registryFloor'
    | 'requiredScope'
    | 'requiredRole'
    | 'idempotency'
  >,
): string {
  const content = stableStringify({
    name: tool.descriptor.name,
    inputSchema: tool.descriptor.inputSchema,
    effect: tool.effect,
    defaultPolicy: tool.defaultPolicy,
    registryFloor: tool.registryFloor,
    requiredScope: tool.requiredScope,
    requiredRole: tool.requiredRole,
    idempotency: tool.idempotency,
  });
  return `v1.${fnv1a64(content).slice(0, 12)}`;
}

const BOTH_SURFACES: readonly NativeToolSurface[] = ['spec', 'mcp'];

function buildRegistry(): readonly NativeToolDefinition[] {
  const classified = new Set(Object.keys(NATIVE_TOOL_METADATA));
  const registry = NATIVE_TOOL_DESCRIPTORS.map((descriptor) => {
    const metadata = NATIVE_TOOL_METADATA[descriptor.name];
    if (!metadata) {
      throw new Error(
        `Tool "${descriptor.name}" has no registry classification. Add it to NATIVE_TOOL_METADATA with an effect, floor, scope, previewer, and redactor before advertising it.`,
      );
    }
    classified.delete(descriptor.name);
    const partial = {
      ...metadata,
      surfaces: metadata.surfaces ?? BOTH_SURFACES,
      key: descriptor.name,
      descriptor,
    };
    return { ...partial, version: nativeToolContractVersion(partial) };
  });

  if (classified.size > 0) {
    throw new Error(
      `NATIVE_TOOL_METADATA classifies tools that are not advertised: ${[...classified].join(', ')}.`,
    );
  }
  return registry;
}

export const NATIVE_TOOL_REGISTRY: readonly NativeToolDefinition[] = buildRegistry();

// A Map, not an object: a bare object lookup would resolve "__proto__" and
// "constructor" to inherited values and hand back a truthy non-tool.
const REGISTRY_BY_NAME = new Map(NATIVE_TOOL_REGISTRY.map((tool) => [tool.descriptor.name, tool]));

export function getNativeTool(name: string): NativeToolDefinition | null {
  return REGISTRY_BY_NAME.get(name) ?? null;
}

/** The registry tools one client may offer, in registry order. */
export function nativeToolsForSurface(surface: NativeToolSurface): readonly NativeToolDefinition[] {
  return NATIVE_TOOL_REGISTRY.filter((tool) => tool.surfaces.includes(surface));
}

export function nativeToolNames(): readonly string[] {
  return NATIVE_TOOL_REGISTRY.map((tool) => tool.descriptor.name);
}

export function nativeToolDescriptors(): NativeToolDefinition['descriptor'][] {
  return NATIVE_TOOL_REGISTRY.map((tool) => tool.descriptor);
}

/**
 * The effect of one specific call. Multiplexed tools classify per call after
 * argument validation; everything else is static.
 */
export function resolveNativeToolEffect(
  tool: NativeToolDefinition,
  args: Readonly<Record<string, unknown>>,
): NativeToolEffect {
  return tool.resolveEffect ? tool.resolveEffect(args) : tool.effect;
}
